import React from 'react';

import {
  GridLegacy as Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateSlashes } from '~/services/TimeService';

import type { DeletedBlackoutRange } from './blackoutReconciliation';
import type { RoomTypeBlackoutPlan } from './buildBlackoutApplyPlan';

interface Props {
  deletedRanges: RoomTypeBlackoutPlan['deletedRanges'];
  rangesToCreate: RoomTypeBlackoutPlan['rangesToCreate'];
}

export default function BlackoutDiffTable({ deletedRanges, rangesToCreate }: Props) {
  if (deletedRanges.length === 0 && rangesToCreate.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {deletedRanges.length > 0 && (
        <Grid item xs>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Existing blackout blocks (to be removed)
          </Typography>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    bgcolor: 'error.dark',
                    '& th': { bgcolor: 'inherit', color: 'white' },
                  }}
                >
                  <TableRow>
                    <TableCell>Range</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedRanges.map((range: DeletedBlackoutRange) => (
                    <TableRow
                      key={`${range.id}-${range.roomRateId}`}
                      sx={{
                        bgcolor: 'error.50',
                        '& td': { color: 'error.dark' },
                      }}
                    >
                      <TableCell>
                        {formatDateSlashes(range.start)} → {formatDateSlashes(range.end)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      )}

      {rangesToCreate.length > 0 && (
        <Grid item xs>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            New blackout ranges (to be created)
          </Typography>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    bgcolor: 'success.dark',
                    '& th': { bgcolor: 'inherit', color: 'white' },
                  }}
                >
                  <TableRow>
                    <TableCell>Range</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rangesToCreate.map((range, index) => (
                    <TableRow
                      key={`${range.roomRateId}-${range.start}-${range.end}-${index}`}
                      sx={{
                        bgcolor: 'success.50',
                        '& td': { color: 'success.dark' },
                      }}
                    >
                      <TableCell>
                        {formatDateSlashes(range.start)} → {formatDateSlashes(range.end)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}
